<script>
import {useOccupationStore} from "@/stores/Occupation";
import {computed, onMounted, ref, watch} from "vue";
import useSharedData from "@/use/SharedData";
import axios from "axios";

export default {
    props: {
        value: [Number, Array],
        multiple: Boolean,
        label: String,
        readonly: Boolean,
        external: Boolean,
        externalLoading: Boolean,
        externalItems: Array,
        rules: Array,
    },
    setup(props, {emit}) {
        const occupationStore = useOccupationStore();
        const {loading, inputColor, backgroundColor} = useSharedData();
        const occupation = ref(props.multiple ? [] : null);
        
        function findAll() {
            axios.get('/occupations/selections').then(response => {
                occupationStore.setOccupationsSelect(response.data);
            }).finally(() => {
                loading.value = false;
            });
        }
        
        // --- Computed Selection States ---
        const isAllSelected = computed(() => {
            return occupation.value?.length > 0 &&
                occupation.value?.length === items.value.length;
        });
        
        const selectAllIcon = computed(() => {
            if (isAllSelected.value) return 'mdi-close-box';
            if (occupation.value?.length > 0) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        });
        
        // --- Methods ---
        function toggleSelectAll() {
            if (isAllSelected.value) {
                occupation.value = [];
            } else {
                occupation.value = items.value.map(item => item.id);
            }
            onInput();
        }
        
        function removeItem(item) {
            const index = occupation.value.indexOf(item.id);
            if (index >= 0) occupation.value.splice(index, 1);
            onInput();
        }
        
        function onInput() {
            emit('input', occupation.value);
        }
        
        const items = computed(() => props.external ? props.externalItems : occupationStore.occupationsSelect);
        
        watch(() => props.value, (newVal) => {
            occupation.value = newVal || (props.multiple ? [] : null);
        }, {immediate: true});
        
        onMounted(() => {
            if (!props.external) {
                findAll();
            }
        });
        
        return {
            occupationStore,
            items,
            occupation,
            loading,
            inputColor,
            backgroundColor,
            isAllSelected,
            selectAllIcon,
            toggleSelectAll,
            removeItem,
            onInput
        };
    }
};
</script>

<template>
    <div>
        <v-autocomplete
            v-model="occupation"
            :label="label"
            :items="items"
            item-value="id"
            item-text="name"
            outlined
            dense
            :loading="loading || externalLoading"
            :readonly="readonly"
            :multiple="multiple"
            :chips="multiple"
            :clearable="!readonly"
            :color="inputColor"
            :background-color="backgroundColor"
            hide-details="auto"
            @input="onInput"
            :rules="rules"
        >
            <template v-slot:selection="{ item, index }">
                <v-chip v-if="isAllSelected && index === 0" small>
                    <strong>{{ $t('common.all') }}</strong>
                </v-chip>
                
                <v-chip
                    v-else-if="!isAllSelected"
                    close
                    small
                    @click:close="removeItem(item)"
                >
                    {{ item.name }}
                </v-chip>
            </template>
            
            <template v-if="multiple" v-slot:prepend-item>
                <v-list-item ripple @click="toggleSelectAll">
                    <v-list-item-action>
                        <v-icon>{{ selectAllIcon }}</v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                        <v-list-item-title>{{ $t('common.all') }}</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
            </template>
        </v-autocomplete>
        
        <input type="hidden"/>
    </div>
</template>